import { PubSubService } from '../_shared/pubSubServiceInterface';
import { DicomPrintConfigType, FilmSize, filmSizeMap } from './types';

const name = 'dicomPrintService';

/** Interface cho các hàm được Provider inject vào */
type DicomPrintServiceImplementation = {
	_getState?: () => DicomPrintConfigType;
	_setFilmSize?: (filmSize: FilmSize) => void;
	_setDpi?: (dpi: number) => void;
	_reset?: () => void;
	_getViewportRatio: () => number;
	_setPrinter?: (printer: number) => void;
	_setOrientation?: (orientation: string) => void;
	_onPrintDicom: (printConfig: DicomPrintConfigType) => void;
	_toggleDicomPrintMode?: () => void;
};

/**
 * DicomPrintService
 */
class DicomPrintService extends PubSubService {
	public static readonly EVENTS = {
		ENTERED_FILM_PRINTING_MODE: 'event::enteredFilmPrintingMode',
		STATE_RESET: 'event::dicomPrintStateChanged',
		FILM_SIZE_CHANGED: 'event::dicomPrintFilmSizeChanged',
		DPI_CHANGED: 'event::dicomPrintDpiChanged',
		ORIENTATION_CHANGED: 'event::dicomPrintOrientationChanged',
		PRINTER_CHANGED: 'event::dicomPrintPrinterChanged',
	};

	public static REGISTRATION = {
		name,
		altName: 'DicomPrintService',
		create: () => new DicomPrintService(),
	};

	private serviceImplementation: DicomPrintServiceImplementation = {} as DicomPrintServiceImplementation;

	constructor() {
		super(DicomPrintService.EVENTS);
	}

	/** Cho phép Provider/extension inject logic quản lý state */
	public setServiceImplementation({
		getState,
		setFilmSize,
		setDpi,
		reset,
		setPrinter,
		setOrientation,
		toggleDicomPrintMode,
	}: {
		getState?: () => DicomPrintConfigType;
		setFilmSize?: (filmSize: FilmSize) => void;
		setDpi?: (dpi: number) => void;
		reset?: () => void;
		setPrinter?: (printer: number) => void;
		setOrientation?: (orientation: string) => void;
		toggleDicomPrintMode?: () => void;
	}) {
		if (getState) {
			this.serviceImplementation._getState = getState;
		}
		if (setFilmSize) {
			this.serviceImplementation._setFilmSize = setFilmSize;
		}
		if (setDpi) {
			this.serviceImplementation._setDpi = setDpi;
		}
		if (setOrientation) {
			this.serviceImplementation._setOrientation = setOrientation;
		}
		if (setPrinter) {
			this.serviceImplementation._setPrinter = setPrinter;
		}
		if (reset) {
			this.serviceImplementation._reset = reset;
		}
		if (reset) {
			this.serviceImplementation._toggleDicomPrintMode = toggleDicomPrintMode;
		}
	}

	/** Lấy state từ provider */
	public getState(): DicomPrintConfigType {
		if (!this.serviceImplementation._getState) {
			throw new Error('DicomPrintService: getState() chưa được Provider gán');
		}
		return this.serviceImplementation._getState();
	}

	/** Lấy kích thước film theo inch */
	private getFilmSizeInInches(filmSize: FilmSize) {
		return filmSizeMap[filmSize];
	}
	/** Reset cấu hình in về mặc định */
	public reset() {
		this.serviceImplementation._reset?.();
		this._broadcastEvent(DicomPrintService.EVENTS.STATE_RESET, { reset: true });
	}

	/** Tỷ lệ khung hình của film */
	public getViewportRatio(filmSize: FilmSize) {
		if (this.serviceImplementation._getViewportRatio) {
			this.serviceImplementation._getViewportRatio();
			this.getState().filmSize;
			const film = this.getFilmSizeInInches(filmSize);
			return film.widthInch / film.heightInch;
		}
		return null;
	}

	/** Tính kích thước pixel từ filmSize + DPI + Orientation */
	public getImagePixelSizeFromFilm({
		filmSize,
		filmOrientation,
		dpi,
	}: Pick<DicomPrintConfigType, 'filmSize' | 'filmOrientation' | 'dpi'>) {
		const film = filmSizeMap[filmSize];
		if (!film) {
			throw new Error(`Film size không hợp lệ: ${filmSize}`);
		}

		const isLandscape = filmOrientation === 'LANDSCAPE';
		const widthInch = isLandscape ? film.heightInch : film.widthInch;
		const heightInch = isLandscape ? film.widthInch : film.heightInch;

		return {
			widthPx: Math.round(widthInch * dpi),
			heightPx: Math.round(heightInch * dpi),
		};
	}

	public setOrientation(orientation: DicomPrintConfigType['filmOrientation']) {
		if (this.serviceImplementation._setOrientation) {
			this.serviceImplementation._setOrientation(orientation);
			this._broadcastEvent(DicomPrintService.EVENTS.ORIENTATION_CHANGED, { orientation });
		}
	}
	public setDpi(dpi: DicomPrintConfigType['dpi']) {
		if (this.serviceImplementation._setDpi) {
			this.serviceImplementation._setDpi(dpi);
			this._broadcastEvent(DicomPrintService.EVENTS.DPI_CHANGED, { dpi });
		}
	}
	public setFilmSize(filmSize: DicomPrintConfigType['filmSize']) {
		if (this.serviceImplementation._setFilmSize) {
			this.serviceImplementation._setFilmSize(filmSize);
			this._broadcastEvent(DicomPrintService.EVENTS.FILM_SIZE_CHANGED, { filmSize });
		}
	}
	public setPrinter(printer: DicomPrintConfigType['dicomPrinterID']) {
		if (this.serviceImplementation._setPrinter) {
			this.serviceImplementation._setPrinter(printer);
			this._broadcastEvent(DicomPrintService.EVENTS.PRINTER_CHANGED, { printer });
		}
	}

	public toggleDicomPrintMode() {
		if (this.serviceImplementation._toggleDicomPrintMode) {
			this.serviceImplementation._toggleDicomPrintMode();
			this._broadcastEvent(DicomPrintService.EVENTS.ENTERED_FILM_PRINTING_MODE, {});
		}
	}
}

export default DicomPrintService;
